/**
 * Admin › Network
 * Manage the four sibling sites (Social Pulse + Kerala + Food Pulse +
 * India's). Non-technical admins can edit brand info, palette accent,
 * and enabled features from one screen — no `.env` edits, no mongo
 * shell.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Loader2, RotateCcw, Check, Globe, ExternalLink, Rocket } from "lucide-react";

const ALL_FEATURES = ["family_tree", "memorials", "causes", "pulse_items", "marketplace"];

export default function AdminNetworkSites() {
  const [rows, setRows]   = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [busy, setBusy]   = useState(true);
  const [editing, setEditing] = useState(null); // slug

  const load = async () => {
    setBusy(true);
    try {
      const [{ data: sites }, { data: stats }] = await Promise.all([
        http.get("/admin/site-configs"),
        http.get("/admin/site-configs/analytics").catch(() => ({ data: { items: [] } })),
      ]);
      setRows(sites.items || []);
      const byslug = {};
      for (const s of stats.items || []) byslug[s.site_source] = s;
      setAnalytics(byslug);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load network."); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const reset = async (slug) => {
    if (!window.confirm(`Revert ${slug} to its hard-coded defaults? Your custom brand fields will be lost.`)) return;
    try {
      await http.delete(`/admin/site-configs/${slug}`);
      toast.success(`${slug} reverted to defaults.`);
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to reset."); }
  };

  return (
    <div data-testid="admin-network-sites">
      <div className="mb-4">
        <div className="overline mb-2">CMS · NETWORK OF SITES</div>
        <h2 className="font-display text-2xl">Sibling sites</h2>
        <p className="text-sm opacity-70 mt-1 max-w-2xl">
          Every site in the Social Pulse network reads its brand, palette accent, and enabled features from the central API. Edit here and the change propagates on the sibling site's next page load — no coordinated redeploy needed. The strip on each card shows publish activity in the last 7 days so you can spot broken integrations at a glance.
        </p>
      </div>

      {busy && <div className="text-sm opacity-60"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>}

      <div className="grid gap-4 md:grid-cols-2">
        {rows.map((s) => (
          <SiteCard
            key={s.site_source}
            site={s}
            stats={analytics[s.site_source]}
            isEditing={editing === s.site_source}
            onEdit={() => setEditing(s.site_source)}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
            onReset={() => reset(s.site_source)}
          />
        ))}
      </div>
    </div>
  );
}


function SiteCard({ site, stats, isEditing, onEdit, onCancel, onSaved, onReset }) {
  const [form, setForm] = useState(site);
  useEffect(() => { setForm(site); }, [site, isEditing]);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleFeature = (f) => {
    const cur = new Set(form.features || []);
    if (cur.has(f)) cur.delete(f); else cur.add(f);
    set("features", Array.from(cur));
  };

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      // Only send fields that changed vs the current row.
      for (const k of ["name","tagline","primary_domain","logo_url","favicon_url","primary_market","footer_text","accent_primary","accent_secondary"]) {
        if ((form[k] || "") !== (site[k] || "")) patch[k] = form[k] || null;
      }
      if (JSON.stringify((form.features || []).sort()) !== JSON.stringify((site.features || []).sort())) {
        patch.features = form.features;
      }
      if (Object.keys(patch).length === 0) {
        toast.info("Nothing changed.");
        onCancel();
        return;
      }
      await http.put(`/admin/site-configs/${site.site_source}`, patch);
      toast.success(`${site.name} updated.`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  const accent = form.accent_primary || site.accent_primary || "#B25D38";
  const primaryDomain = site.primary_domain || "";

  return (
    <div
      className="rounded-2xl border p-5 bg-white"
      style={{ borderColor: "var(--wofy-border)" }}
      data-testid={`network-site-${site.site_source}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center font-display text-lg text-white"
            style={{ background: accent }}
            aria-hidden
          >
            {(site.name || site.site_source).slice(0, 1).toUpperCase()}
          </div>
          <div>
            <div className="font-display text-xl leading-tight">{site.name}</div>
            <a href={primaryDomain} target="_blank" rel="noreferrer" className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1">
              <Globe className="w-3 h-3" />
              {primaryDomain?.replace(/^https?:\/\//, "")}
              <ExternalLink className="w-3 h-3 opacity-50" />
            </a>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <span
            className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full ${site._has_override ? "" : "opacity-60"}`}
            style={{ background: site._has_override ? "#EDF2E9" : "rgba(0,0,0,0.04)", color: site._has_override ? "#065F46" : "inherit" }}
            data-testid={`override-badge-${site.site_source}`}
          >
            {site._has_override ? "Customised" : "Default"}
          </span>
          {site.site_source !== "socialpulse" && !isEditing && (
            <Link
              to={`/admin/network/launch/${site.site_source}`}
              className="text-xs rounded-full px-3 py-1 inline-flex items-center gap-1 font-medium"
              style={{ background: "#B25D38", color: "white" }}
              data-testid={`network-launch-${site.site_source}`}
            >
              <Rocket className="w-3 h-3" /> Launch site
            </Link>
          )}
          {!isEditing ? (
            <button onClick={onEdit} className="text-xs rounded-full border px-3 py-1 hover:bg-black/5" style={{ borderColor: "var(--wofy-border)" }} data-testid={`network-edit-${site.site_source}`}>
              Edit
            </button>
          ) : null}
          {site._has_override && !isEditing && (
            <button onClick={onReset} className="text-xs rounded-full border px-3 py-1 inline-flex items-center gap-1 hover:bg-black/5" style={{ borderColor: "var(--wofy-border)" }} data-testid={`network-reset-${site.site_source}`}>
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          )}
        </div>
      </div>

      {!isEditing ? (
        <>
          <p className="text-sm mt-3 leading-snug" style={{ color: "var(--wofy-text-soft)" }}>{site.tagline}</p>
          <ActivityStrip stats={stats} accent={accent} />
          <div className="mt-4 flex flex-wrap gap-1.5">
            {(site.features || []).map((f) => (
              <span key={f} className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full" style={{ background: "rgba(0,0,0,0.04)" }}>{f.replace(/_/g, " ")}</span>
            ))}
          </div>
        </>
      ) : (
        <div className="mt-4 space-y-3">
          <F label="Brand name" value={form.name || ""} onChange={(v) => set("name", v)} testid={`inp-name-${site.site_source}`} />
          <F label="Tagline" value={form.tagline || ""} onChange={(v) => set("tagline", v)} multiline testid={`inp-tagline-${site.site_source}`} />
          <div className="grid grid-cols-2 gap-3">
            <F label="Primary domain" value={form.primary_domain || ""} onChange={(v) => set("primary_domain", v)} testid={`inp-domain-${site.site_source}`} />
            <F label="Primary market" value={form.primary_market || ""} onChange={(v) => set("primary_market", v)} testid={`inp-market-${site.site_source}`} />
            <F label="Logo URL" value={form.logo_url || ""} onChange={(v) => set("logo_url", v)} testid={`inp-logo-${site.site_source}`} />
            <F label="Favicon URL" value={form.favicon_url || ""} onChange={(v) => set("favicon_url", v)} testid={`inp-favicon-${site.site_source}`} />
          </div>
          <F label="Footer text" value={form.footer_text || ""} onChange={(v) => set("footer_text", v)} testid={`inp-footer-${site.site_source}`} />

          <div>
            <div className="text-[10px] font-mono uppercase opacity-60 mb-1.5">Palette accent (design tokens)</div>
            <div className="grid grid-cols-2 gap-3">
              <ColorInput label="Primary" value={form.accent_primary || ""} onChange={(v) => set("accent_primary", v)} testid={`inp-primary-${site.site_source}`} />
              <ColorInput label="Secondary" value={form.accent_secondary || ""} onChange={(v) => set("accent_secondary", v)} testid={`inp-secondary-${site.site_source}`} />
            </div>
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase opacity-60 mb-1.5">Enabled features</div>
            <div className="flex flex-wrap gap-2">
              {ALL_FEATURES.map((f) => {
                const on = (form.features || []).includes(f);
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => toggleFeature(f)}
                    className={`text-xs rounded-full px-3 py-1 border inline-flex items-center gap-1 ${on ? "" : "opacity-50"}`}
                    style={{ borderColor: "var(--wofy-border)", background: on ? "#EDF2E9" : "white" }}
                    data-testid={`feat-${site.site_source}-${f}`}
                  >
                    {on && <Check className="w-3 h-3" />} {f.replace(/_/g, " ")}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={onCancel} className="text-xs opacity-70 hover:opacity-100" data-testid={`network-cancel-${site.site_source}`}>Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              className="text-xs rounded-full px-4 py-1.5 font-medium"
              style={{ background: accent, color: "white", opacity: saving ? 0.6 : 1 }}
              data-testid={`network-save-${site.site_source}`}
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function F({ label, value, onChange, multiline = false, testid }) {
  return (
    <label className="block text-xs">
      <span className="opacity-70">{label}</span>
      {multiline ? (
        <textarea
          rows={2}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full rounded border px-2 py-1.5 text-sm bg-white"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid={testid}
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full rounded border px-2 py-1.5 text-sm bg-white"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid={testid}
        />
      )}
    </label>
  );
}


function ColorInput({ label, value, onChange, testid }) {
  return (
    <label className="block text-xs">
      <span className="opacity-70">{label}</span>
      <div className="mt-1 flex items-center gap-2 rounded border px-2 py-1 bg-white" style={{ borderColor: "var(--wofy-border)" }}>
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(value || "") ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent"
          data-testid={`${testid}-swatch`}
        />
        <input
          value={value || ""}
          placeholder="#B25D38 or empty for default"
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 text-sm outline-none bg-transparent font-mono"
          data-testid={testid}
        />
      </div>
    </label>
  );
}


/**
 * ActivityStrip — 4 tiny counters + a coloured health dot showing how
 * active each sibling has been in the last 7 days. Green = healthy
 * (published in last 24h). Amber = quiet (last 7d but nothing in
 * 24h). Red = silent (nothing in the last 7 days — probably a broken
 * integration).
 */
function ActivityStrip({ stats, accent }) {
  if (!stats) return null;
  const HEALTH = {
    healthy: { color: "#065F46", bg: "#D1FAE5", label: "Active" },
    quiet:   { color: "#B45309", bg: "#FEF3C7", label: "Quiet" },
    silent:  { color: "#B23A3A", bg: "#FEE2E2", label: "Silent" },
  };
  const h = HEALTH[stats.health] || HEALTH.silent;
  const rel = (iso) => {
    if (!iso) return "never";
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 48) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };
  return (
    <div
      className="mt-4 rounded-lg border p-2.5 flex items-center gap-3 text-[11px] flex-wrap"
      style={{ borderColor: "var(--wofy-border)", background: "rgba(0,0,0,0.02)" }}
      data-testid={`activity-${stats.site_source}`}
    >
      <div className="inline-flex items-center gap-1.5 shrink-0" title={`Publish activity: ${h.label}`}>
        <span className="inline-block w-2 h-2 rounded-full" style={{ background: h.color }} aria-hidden />
        <span className="font-mono uppercase text-[9px] font-medium" style={{ color: h.color }}>{h.label}</span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 flex-1">
        <Metric label="Pulse" value={stats.pulse_7d} accent={accent} />
        <Metric label="Causes" value={stats.cause_7d} accent={accent} />
        <Metric label="Memorials" value={stats.memorial_7d} accent={accent} />
        <Metric label="Articles" value={stats.article_7d} accent={accent} />
      </div>
      <div className="opacity-60 shrink-0 font-mono text-[9px]" title={stats.last_seen_at || ""}>
        {stats.last_seen_at ? `Last: ${rel(stats.last_seen_at)}` : "No content yet"}
      </div>
    </div>
  );
}


function Metric({ label, value, accent }) {
  const zero = !value;
  return (
    <div className="inline-flex items-baseline gap-1">
      <span className="font-mono font-semibold tabular-nums" style={{ color: zero ? "#94A3B8" : accent }}>{value ?? 0}</span>
      <span className="opacity-70">{label}</span>
    </div>
  );
}

